// =============================================================================
// AtRiskUnitsWidget — every unit that needs eyes today.
//
// Combines three risk buckets: at-risk (< 12.0V), warning (12.0–12.39V or
// trending down), and stale (no check in 7+ days). Sorted with risk first,
// then warn, then stale. Each row click navigates to /check-in/:id so the
// porter is one click from putting a meter on it.
// =============================================================================
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useBatteryUnits, partitionByRisk, daysSinceCheck, isDropping, voltageBucket, unitName,
} from "../lib/useBatteryData.js";

const BUCKET_LABEL = { risk: "At risk", warn: "Watch", stale: "Stale" };

export function AtRiskUnitsWidget({ config }) {
  const nav = useNavigate();
  const { data: units, loading } = useBatteryUnits();
  const limit = config?.limit || 10;

  const rows = useMemo(() => {
    const { risk, warn, stale } = partitionByRisk(units);
    return [
      ...risk.map((u) => ({ ...u, _bucket: "risk" })),
      ...warn.map((u) => ({ ...u, _bucket: "warn" })),
      ...stale.map((u) => ({ ...u, _bucket: "stale" })),
    ].slice(0, limit);
  }, [units, limit]);

  return (
    <div className="w-shell">
      <header className="w-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div>
          <div className="w-eye">
            <span className="w-eye-dot" />
            NEEDS YOUR EYES · {loading ? "loading…" : `${rows.length} ${rows.length === 1 ? "UNIT" : "UNITS"}`}
          </div>
          <h3 className="w-h3">
            {rows.length === 1
              ? <><em>One</em> unit wants your eyes.</>
              : <><em>{rows.length}</em> units want your eyes.</>}
          </h3>
        </div>
        {!loading && rows.length > 0 && (
          <button
            type="button"
            className="w-walk-cta"
            onClick={(e) => { e.stopPropagation(); nav("/walk"); }}
          >
            Start a walk →
          </button>
        )}
      </header>

      {loading && <div className="w-loading">Loading units…</div>}

      {!loading && rows.length === 0 && (
        <div className="w-empty">
          <div className="w-empty-h">Nothing flagged.</div>
          <div className="w-empty-s">Every unit is in spec, on time, and trending the right way.</div>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="w-list">
          {rows.map((u) => {
            const v = Number(u.voltage);
            const dipping = isDropping(u);
            const days = daysSinceCheck(u);
            return (
              <button
                key={u.id}
                type="button"
                className={`w-row${u._bucket === 'risk' ? ' over' : u._bucket === 'warn' ? ' warn' : ''}`}
                onClick={() => nav(`/check-in/${u.id}`)}
              >
                <div className="w-row-av">
                  <svg width="22" height="14" viewBox="0 0 40 20" aria-hidden="true">
                    <path d="M 4 16 L 8 9 L 14 7 L 30 7 L 34 9 L 36 14 L 38 16 L 6 16 Z" fill="currentColor" opacity="0.18" stroke="currentColor" strokeWidth="0.7" />
                    <circle cx="12" cy="16" r="2" fill="none" stroke="currentColor" strokeWidth="0.7" />
                    <circle cx="30" cy="16" r="2" fill="none" stroke="currentColor" strokeWidth="0.7" />
                  </svg>
                </div>
                <div className="w-row-main">
                  <div className="w-row-name">{unitName(u)}</div>
                  <div className="w-row-sub">
                    {u.stockNumber || "—"}
                    {dipping ? " · ↓ dropping" : ""}
                    {days !== null ? ` · ${days}d since check` : " · no check yet"}
                  </div>
                </div>
                <div className="w-row-age" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {Number.isFinite(v) ? `${v.toFixed(2)}V` : "—"}
                </div>
                <div className="w-row-tag"><span className={`w-pill${u._bucket === 'risk' ? ' over' : u._bucket === 'warn' ? ' warn' : ''}`}>{BUCKET_LABEL[u._bucket]}</span></div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
